import { useState, useEffect } from 'react';
import './App.css';
import { Chart } from "react-google-charts";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import DisplayBox from './components/DisplayBox';
import LoadingSpinner from './components/LoadingSpinner';

function App() {
  const now = new Date();
  const defaultStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 28); // 28 days ago
  const defaultEndDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0); // Midnight last night

  const [isLoading, setIsLoading] = useState(true);
  const [runningUsageCost, setRunningUsageCost] = useState(0);
  const [runningGenCost, setRunningGenCost] = useState(0);
  const [startDate, setStartDate] = useState(defaultStartDate); // Set default start date
  const [endDate, setEndDate] = useState(defaultEndDate); // Set default end date
  const [savedStartDate, setSavedStartDate] = useState();
  const [savedEndDate, setSavedEndDate] = useState();
  const [chartData, setChartData] = useState({});  
  const [standingChargeTotal, setStandingChargeTotal] = useState(0);
  
  const options = {
    vAxis: {title:"Amount (pence)"},
    hAxis: {
      title:"Date",
      format: "dd-MMM-yy HH:MM",
      ticks: "dateTicks"
    },
    curveType: "function",
    legend: {position:"bottom"},
    animation: {
      duration: 1000,
      easing: "out",
      startup: true,
    },
  };

  async function getData() {
    console.log("Start Date:", startDate);
    console.log("End Date:", endDate);
  
    let newData = [[{ type: "datetime", label: "Day" }, "Electricity Used", "Electricity Generated"]];
    let usageTariff = [];
    let genTariff = [];
    const sd = startDate?.toISOString();
    const ed = endDate?.toISOString();
  
    const timeDiff = endDate?.getTime() - startDate?.getTime();
    if (timeDiff > -1) {
      const points = timeDiff / (1000 * 60 * 60) * 2;
  
      try {
        // Fetch standing charges
        const tariffResponse = await fetch(
          `https://api.octopus.energy/v1/products/${process.env.REACT_APP_USAGE_PRODUCT_CODE}/electricity-tariffs/${process.env.REACT_APP_USAGE_TARIFF_CODE}/standing-charges/`,
          {
            headers: {
              Authorization: `Basic ${btoa(`${process.env.REACT_APP_OCTOPUS_API_KEY}:`)}`,
            },
          }
        );
        const tariffData = await tariffResponse.json();
        console.log("Standing Charges Data:", tariffData); // Debugging log
        setStandingChargeTotal((tariffData.results[0].value_inc_vat) / 48 * points);
  
        // Fetch electricity used data
        const electricityUsedResponse = await fetch(
          `https://api.octopus.energy/v1/electricity-meter-points/2000025971810/meters/19L2020756/consumption?page_size=${points}&period_from=${sd}&period_to=${ed}&order_by=period`,
          {
            headers: {
              Authorization: `Basic ${btoa(`${process.env.REACT_APP_OCTOPUS_API_KEY}:`)}`,
            },
          }
        );
        const electricityUsedData = await electricityUsedResponse.json();
        console.log("Electricity Used Data:", electricityUsedData); // Debugging log

        // Fetch electricity generated data
        const electricityGeneratedResponse = await fetch(
          `https://api.octopus.energy/v1/electricity-meter-points/2000060337514/meters/19L2020756/consumption?page_size=${points}&period_from=${sd}&period_to=${ed}&order_by=period`,
          {
            headers: {
              Authorization: `Basic ${btoa(`${process.env.REACT_APP_OCTOPUS_API_KEY}:`)}`,
            },
          }
        );
        const electricityGeneratedData = await electricityGeneratedResponse.json();
        console.log("Electricity Generated Data:", electricityGeneratedData); // Debugging log

        // Fetch usage tariff data
        const usageTariffResponse = await fetch(
          `https://api.octopus.energy/v1/products/${process.env.REACT_APP_USAGE_PRODUCT_CODE}/electricity-tariffs/${process.env.REACT_APP_USAGE_TARIFF_CODE}/standard-unit-rates?page_size=${points}&period_from=${sd}&period_to=${ed}`,
          {
            headers: {
              Authorization: `Basic ${btoa(`${process.env.REACT_APP_OCTOPUS_API_KEY}:`)}`,
            },
          }
        );
        const usageTariffData = await usageTariffResponse.json();
        console.log("Usage Tariff Data:", usageTariffData); // Debugging log

        // Fetch generation tariff data
        const genTariffResponse = await fetch(
          `https://api.octopus.energy/v1/products/${process.env.REACT_APP_GEN_PRODUCT_CODE}/electricity-tariffs/${process.env.REACT_APP_GEN_TARIFF_CODE}/standard-unit-rates/`,
          {
            headers: {
              Authorization: `Basic ${btoa(`${process.env.REACT_APP_OCTOPUS_API_KEY}:`)}`,
            },
          }
        );
        const genTariffData = await genTariffResponse.json();
  
        // Process tariff data
        if (
          electricityUsedData.results &&
          electricityGeneratedData.results &&
          usageTariffData.results &&
          genTariffData.results
        ) {
          for (let i = 0; i < usageTariffData.results.length; i++) {
            usageTariff.push([
              Date.parse(usageTariffData.results[i].valid_from),
              usageTariffData.results[i].value_inc_vat,
            ]);
          }
          for (let i = 0; i < genTariffData.results.length; i++) {
            genTariff.push([
              Date.parse(genTariffData.results[i].valid_from),
              Date.parse(genTariffData.results[i].valid_to),
              genTariffData.results[i].value_inc_vat,
            ]);
          }
  
          let tempRunningUsageCost = 0;
          let unitRate = 0;
          for (let i = 0; i < electricityGeneratedData.results.length - 1; i++) {
            unitRate = usageTariff.find(
              (elem) => elem[0] === Date.parse(electricityUsedData.results[i].interval_start)
            );
            if (unitRate) {
              const t = [
                electricityUsedData.results[i].interval_start,
                electricityUsedData.results[i].consumption * -1 * unitRate[1],
                0,
              ];
              tempRunningUsageCost += t[1];
              newData.push(t);
            }
          }
  
          setRunningUsageCost(tempRunningUsageCost);
  
          let tempRunningGenCost = 0;
          electricityGeneratedData.results.forEach((element) => {
            for (let i = 1; i < newData.length - 1; i++) {
              if (newData[i][0] === element.interval_start) {
                const thisDate = Date.parse(newData[i][0]);
  
                unitRate = genTariff.find((elem) => {
                  return elem[0] < thisDate && (elem[1] > thisDate || isNaN(elem[1]));
                });
  
                newData[i][2] = element.consumption * unitRate[2];
                tempRunningGenCost += newData[i][2];
                break;
              }
            }
          });
          setRunningGenCost(tempRunningGenCost);
  
          newData.slice(1).forEach((x) => {
            x[0] = new Date(Date.parse(x[0]));
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    return newData;
  }

  useEffect(() => {    
    setIsLoading(true);
  }, [startDate])

  useEffect(() => {    
    setIsLoading(true);

  }, [endDate])

  useEffect(() => {
    if (startDate && endDate) {
      console.log("Fetching data with dates:", startDate, endDate);
      setIsLoading(true);
      getData().then((result) => {
        if (result.length > 1) {
          setSavedStartDate(startDate);
          setSavedEndDate(endDate);
          setChartData(result);
        } else {
          console.error("No data returned from getData");
        }
      }).catch((error) => {
        console.error("Error fetching data:", error);
      })
      .finally(() => {
        setIsLoading(false); // Ensure loading state is reset after fetching data
      });
    }
  }, [startDate, endDate])

  useEffect(() => {
    setIsLoading(false);

  }, [chartData])

  return (
    <main>
      <div className='title'>⚡ MyEnergy ⚡ </div>
      
      
      <div className='theChart'>
        <div className='chartTitle'>
          <p className='heading'>Energy costings</p>
          <div className="dates">
            <label htmlFor="startDate">Display information from </label>
            <DatePicker
              selected={startDate}
              onChange={(date) => {
                console.log("Selected date:", date); // Debugging
                setStartDate(date);
              }}
              dateFormat="dd/MM/yyyy"
              disabled={isLoading}
              className="date-picker"
            />
            <label htmlFor="endDate"> to </label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              dateFormat="dd/MM/yyyy"
              disabled={isLoading}
              className="date-picker"
            />
          </div>
        </div>
        {isLoading 
          ? <LoadingSpinner /> 
          : <Chart
            chartType={"LineChart"}
            width="100%"
            height="400px"
            data={chartData}
            options={options}
          />
        }
      </div>
      <div className='summary'>
        <DisplayBox
          title="From Grid ⬇"
          value={(runningUsageCost*-1)}
          textColour="red"
        />
        <DisplayBox
          title="Standing Charge"
          value={standingChargeTotal}
          textColour="red"
        />
        <DisplayBox
          title="Exported ⬆"
          value={runningGenCost}
          textColour="green"
        />
        <DisplayBox
          title="Total"
          value={(runningGenCost + runningUsageCost - standingChargeTotal)}
          textColour={(runningGenCost + runningUsageCost - standingChargeTotal) > 0 ? "green" : "red"}
        />
        
        {/* <div className='dataPoints'>
          <label htmlFor="points">If not all data is shown as expected, try increasing this number.<br/>Note, this will have an impact on performance.<br/></label>
          <input 
            className='dataPointsInput'
            placeholder='data points'
            name="points"
            value={tempPoints}
            onChange={handlePointsChange}
          />
        </div> */}
      </div>
      <br/>
      <p className="copyright">
        <a href="https://www.jonnus.co.uk">© Jonnus 2023</a>
      </p>
      
    </main>
    
  );
}

export default App;